<template>
  <div id="app">
    <router-view/>
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
/* ==========================================================================
   全局设计变量
   --------------------------------------------------------------------------
   --header-height 是页头的唯一高度来源：
     页头自身、区块的 scroll-margin-top（锚点跳转要让开页头）、
     以及各页首屏的上边距，都统一引用它，避免多处硬编码后改不干净。
   修改页头高度时只需改这一处（移动端可在各自媒体查询里覆盖）。
   ========================================================================== */
:root {
  --header-height: 64px;
  /* 卡片圆角：全站统一，改这一处即可调整所有卡片类组件的圆角 */
  --radius-card: 14px;
  --radius-sm: 10px;
}

/*
  移动端页头略矮，保证小屏上不显臃肿。
  注意：这条覆盖必须写在全局样式里——若写在组件的 scoped 样式中，
  会被编译成 :root[data-v-xxx]，而该属性并不存在于 <html> 上，规则不会生效。
*/
@media (max-width: 760px) {
  :root {
    --header-height: 60px;
  }
}

/* Global Reset */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/*
 * 字体栈说明
 * ----------
 * 首选 Inter：本项目未自带字体文件，也未引外部字体链接，因此 Inter 仅在
 * 「系统恰好安装」时生效；如需精确还原设计稿的字面效果，请自托管字体
 * （放 static/fonts/ 并补 @font-face，不要新增 npm 依赖）。
 *
 * 回退链按实际可用性排列，避免出现「声明了却永远匹配不上」的死条目：
 *   - Windows：Inter(可选) → Noto Sans SC(可选) → Microsoft YaHei UI → Segoe UI
 *   - macOS  ：Inter(可选) → PingFang SC → Helvetica Neue
 *   - 中文   ：Noto Sans SC / Source Han Sans SC / PingFang SC / Microsoft YaHei UI
 *   - 兜底   ：system-ui / sans-serif
 */
#app {
  font-family: Inter, "Inter var", "Noto Sans SC", "Source Han Sans SC",
    "PingFang SC", "Microsoft YaHei UI", "Microsoft YaHei", "Segoe UI",
    system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #111b2d;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
}

html, body {
  margin: 0;
  padding: 0;
  border: 0;
  background-color: #f8f9fb;
  font-size: 16px;
  line-height: 1.5;
  color: #111b2d;
}

html {
  scroll-behavior: smooth;
}
</style>

<!--
  v-reveal 全局样式（非 scoped：指令挂在各组件模板上，类名需要全局生效）

  约定
  ----
  .rv     初始态：仅设置 opacity/transform，不触发布局
  .rv-on  进入视口后的终态（由 directives/reveal.js 添加，只加一次）

  各组件通过变量定制：
  --rv-y / --rv-x   位移量（默认向上 24px）
  --rv-delay        错峰延迟
  --rv-dur          时长（默认 .7s）
-->
<style>
.rv {
  --rv-y: 24px;
  --rv-x: 0px;
  --rv-s: 1;
  --rv-dur: .7s;
  --rv-delay: 0s;
  --rv-ease: cubic-bezier(.22, .61, .36, 1);
  opacity: 0;
  transition:
    opacity var(--rv-dur) var(--rv-ease) var(--rv-delay),
    transform var(--rv-dur) var(--rv-ease) var(--rv-delay);
  will-change: transform, opacity;
}

.rv[data-rv='fade-slide'] { transform: translate3d(var(--rv-x), var(--rv-y), 0); }
.rv[data-rv='fade'] { transform: none; }
.rv[data-rv='scale'] { transform: translate3d(var(--rv-x), var(--rv-y), 0) scale(.985); }
.rv[data-rv='stagger'] { opacity: 1; transition: none; }

.rv.rv-on {
  opacity: 1;
  transform: translate3d(0, 0, 0) scale(1);
}

/* stagger：父级本身不动，子元素依次入场 */
.rv[data-rv='stagger'] > * {
  opacity: 0;
  transform: translate3d(var(--rv-cx, 0px), var(--rv-cy, 20px), 0);
  transition:
    opacity var(--rv-dur) var(--rv-ease),
    transform var(--rv-dur) var(--rv-ease);
}
.rv[data-rv='stagger'].rv-on > * {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}
.rv[data-rv='stagger'].rv-on > *:nth-child(1) { transition-delay: calc(var(--rv-step, .07s) * 0); }
.rv[data-rv='stagger'].rv-on > *:nth-child(2) { transition-delay: calc(var(--rv-step, .07s) * 1); }
.rv[data-rv='stagger'].rv-on > *:nth-child(3) { transition-delay: calc(var(--rv-step, .07s) * 2); }
.rv[data-rv='stagger'].rv-on > *:nth-child(4) { transition-delay: calc(var(--rv-step, .07s) * 3); }
.rv[data-rv='stagger'].rv-on > *:nth-child(5) { transition-delay: calc(var(--rv-step, .07s) * 4); }
.rv[data-rv='stagger'].rv-on > *:nth-child(6) { transition-delay: calc(var(--rv-step, .07s) * 5); }
.rv[data-rv='stagger'].rv-on > *:nth-child(7) { transition-delay: calc(var(--rv-step, .07s) * 6); }
.rv[data-rv='stagger'].rv-on > *:nth-child(8) { transition-delay: calc(var(--rv-step, .07s) * 7); }
.rv[data-rv='stagger'].rv-on > *:nth-child(n+9) { transition-delay: calc(var(--rv-step, .07s) * 8); }

/* 减少动态效果：不产生任何位移与淡入，内容始终可见 */
@media (prefers-reduced-motion: reduce) {
  .rv,
  .rv[data-rv='fade-slide'],
  .rv[data-rv='fade'],
  .rv[data-rv='scale'],
  .rv[data-rv='stagger'] > * {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}

/* ==========================================================================
   导航链接：悬停下划线自左展开
   - 用 ::after 绘制，绝对定位，不占文档流，因此不改变导航高度与行内间距
   - 只在 hover 与键盘 focus-visible 时展开
   ========================================================================== */
.hover-underline {
  position: relative;
}
.hover-underline::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -5px;
  height: 1px;
  background: currentColor;
  opacity: .55;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform .25s cubic-bezier(.22, .61, .36, 1);
}
.hover-underline:hover::after,
.hover-underline:focus-visible::after {
  transform: scaleX(1);
}

/* ==========================================================================
   键盘可访问性：统一焦点可见样式
   - 用 :focus-visible 而非 :focus，鼠标点击不出现轮廓，只有键盘导航才显示
   ========================================================================== */
a:focus-visible,
button:focus-visible,
[tabindex]:focus-visible {
  outline: 2px solid #1769ff;
  outline-offset: 3px;
}
/* 深色背景区域改用亮色轮廓，保证对比度 */
.site-footer a:focus-visible,
.manifesto a:focus-visible {
  outline-color: #7fabff;
}

@media (prefers-reduced-motion: reduce) {
  .hover-underline::after { transition: none; }
}
</style>



// WEBPACK FOOTER //
// src/App.vue